import React, { useState, useEffect } from 'react';
import { PersonFinal } from '@/api/entities';
import { AccountFinal } from '@/api/entities';
import { useUser } from '@/contexts/UserContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, Eye, EyeOff, CheckCircle2, AlertCircle } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

export default function BatchVisibilityManager() {
  const contextUser = useUser();
  const currentUser = contextUser;
  const [selectedTenant, setSelectedTenant] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [people, setPeople] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentAction, setCurrentAction] = useState('');
  const [results, setResults] = useState(null);
  const { toast } = useToast();

  const tenantOptions = [
    { value: 'instructure', label: 'Instructure' },
    { value: 'clairelabs', label: 'ClaireLabs' },
    { value: 'smowl', label: 'Smowl' },
    { value: 'diambra', label: 'Diambra' },
    { value: 'demarque', label: 'Demarque' },
    { value: 'demarque_V2', label: 'Demarque V2' },
    { value: 'edqwest', label: 'EdQwest' },
    { value: 'faria', label: 'Faria' },
    { value: 'calmedica', label: 'Calmedica' },
    { value: 'prowise', label: 'Prowise' }
  ];

  useEffect(() => {
    if (selectedTenant) {
      loadAccounts();
    }
  }, [selectedTenant]);

  useEffect(() => {
    if (selectedTenant && selectedAccount) {
      loadPeopleForAccount();
    }
  }, [selectedTenant, selectedAccount]);

  const loadAccounts = async () => {
    setIsLoading(true);
    try {
      const allAccounts = await AccountFinal.list();
      
      // Filter accounts by tenant
      const filteredAccounts = allAccounts.filter(acc => {
        if (acc.tenant_id === selectedTenant) return true;
        if (acc.tenant_id === 'global') return true;
        if (selectedTenant === 'instructure' && (acc.tenant_id === 'default' || !acc.tenant_id)) return true;
        if (Array.isArray(acc.visible_to_tenants) && acc.visible_to_tenants.includes(selectedTenant)) return true;
        if (acc.tenant_overrides && acc.tenant_overrides[selectedTenant]) return true;
        return false;
      });

      setAccounts(filteredAccounts.sort((a, b) => 
        (a.account_name || '').localeCompare(b.account_name || '')
      ));
    } catch (error) {
      console.error('Error loading accounts:', error);
      toast({
        title: 'Error',
        description: 'Failed to load accounts',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadPeopleForAccount = async () => {
    setIsLoading(true);
    try {
      const selectedAccObj = accounts.find(a => a.id === selectedAccount);
      if (!selectedAccObj) return;

      // Filter by account_name on the server side for speed
      const linkedPeople = await PersonFinal.filter({ 
        account_name: selectedAccObj.account_name 
      });

      setPeople(linkedPeople);
      console.log(`📋 Found ${linkedPeople.length} people for account:`, selectedAccObj.account_name);
    } catch (error) {
      console.error('Error loading people:', error);
      toast({
        title: 'Error',
        description: 'Failed to load people',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const processBatch = async (peopleToProcess, newVisibility) => {
    const batchSize = 5; // Process 5 at a time
    const delayBetweenBatches = 1000; // 1 second delay
    
    let updated = 0;
    let skipped = 0;
    const errors = [];

    for (let i = 0; i < peopleToProcess.length; i += batchSize) {
      const batch = peopleToProcess.slice(i, i + batchSize);
      
      setCurrentAction(`Processing ${i + 1}-${Math.min(i + batchSize, peopleToProcess.length)} of ${peopleToProcess.length}...`);
      setProgress(Math.round((i / peopleToProcess.length) * 100));

      // Process batch in parallel
      const batchPromises = batch.map(async (person) => {
        try {
          // Check if already in correct state
          if (newVisibility === false && person.is_visible === false) {
            return { status: 'skipped', person };
          }
          if (newVisibility !== false && person.is_visible !== false) {
            return { status: 'skipped', person };
          }

          await PersonFinal.update(person.id, { is_visible: newVisibility });
          return { status: 'updated', person };
        } catch (error) {
          console.error(`Error updating person ${person.id}:`, error);
          return { 
            status: 'error', 
            person, 
            error: error.message || 'Unknown error' 
          };
        }
      });

      const batchResults = await Promise.all(batchPromises);
      
      // Count results
      batchResults.forEach(result => {
        if (result.status === 'updated') updated++;
        else if (result.status === 'skipped') skipped++;
        else if (result.status === 'error') {
          errors.push(`${result.person.first_name} ${result.person.last_name}: ${result.error}`);
        }
      });

      // Delay between batches to avoid rate limiting
      if (i + batchSize < peopleToProcess.length) {
        await new Promise(resolve => setTimeout(resolve, delayBetweenBatches));
      }
    }

    setProgress(100);
    setCurrentAction('Complete!');

    return { updated, skipped, errors };
  };

  const handleHideFromTenant = async () => {
    if (!selectedAccount || !selectedTenant || people.length === 0) {
      toast({
        title: 'Missing selection',
        description: 'Please select both tenant and account',
        variant: 'destructive'
      });
      return;
    }

    setIsProcessing(true);
    setResults(null);
    setProgress(0);
    setCurrentAction('Starting...');

    try {
      const result = await processBatch(people, false);
      
      setResults({
        total: people.length,
        ...result
      });

      toast({
        title: 'Batch update complete',
        description: `Hidden ${result.updated} personas, skipped ${result.skipped}`,
      });

      // Reload people to see updated state
      await loadPeopleForAccount();
    } catch (error) {
      console.error('Batch update error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Batch update failed',
        variant: 'destructive'
      });
    } finally {
      setIsProcessing(false);
      setProgress(0);
      setCurrentAction('');
    }
  };

  const handleShowToTenant = async () => {
    if (!selectedAccount || !selectedTenant || people.length === 0) {
      toast({
        title: 'Missing selection',
        description: 'Please select both tenant and account',
        variant: 'destructive'
      });
      return;
    }

    setIsProcessing(true);
    setResults(null);
    setProgress(0);
    setCurrentAction('Starting...');

    try {
      const result = await processBatch(people, true);
      
      setResults({
        total: people.length,
        ...result
      });

      toast({
        title: 'Batch update complete',
        description: `Showed ${result.updated} personas, skipped ${result.skipped}`,
      });

      // Reload people to see updated state
      await loadPeopleForAccount();
    } catch (error) {
      console.error('Batch update error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Batch update failed',
        variant: 'destructive'
      });
    } finally {
      setIsProcessing(false);
      setProgress(0);
      setCurrentAction('');
    }
  };

  const selectedAccountObj = accounts.find(a => a.id === selectedAccount);
  const visibleCount = people.filter(p => p.is_visible !== false).length;
  const hiddenCount = people.filter(p => p.is_visible === false).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Batch Visibility Manager</CardTitle>
        <CardDescription>
          Hide or show all personas from a specific account for a tenant
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Select Tenant</label>
            <Select value={selectedTenant} onValueChange={setSelectedTenant} disabled={isProcessing}>
              <SelectTrigger>
                <SelectValue placeholder="Choose tenant..." />
              </SelectTrigger>
              <SelectContent>
                {tenantOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Select Account</label>
            <Select 
              value={selectedAccount} 
              onValueChange={setSelectedAccount}
              disabled={!selectedTenant || isLoading || isProcessing}
            >
              <SelectTrigger>
                <SelectValue placeholder={
                  !selectedTenant ? "Select tenant first..." : 
                  isLoading ? "Loading..." :
                  "Choose account..."
                } />
              </SelectTrigger>
              <SelectContent>
                {accounts.map(acc => (
                  <SelectItem key={acc.id} value={acc.id}>
                    {acc.account_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {selectedAccountObj && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">{selectedAccountObj.account_name}</h3>
              <Badge variant="outline">{people.length} personas</Badge>
            </div>
            
            <div className="grid grid-cols-2 gap-2 text-sm mb-4">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-green-600" />
                <span>Visible: {visibleCount}</span>
              </div>
              <div className="flex items-center gap-2">
                <EyeOff className="w-4 h-4 text-red-600" />
                <span>Hidden: {hiddenCount}</span>
              </div>
            </div>

            {isProcessing && (
              <div className="mb-4 space-y-2">
                <Progress value={progress} className="w-full" />
                <p className="text-sm text-gray-600 text-center">{currentAction}</p>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                onClick={handleHideFromTenant}
                disabled={isProcessing || visibleCount === 0}
                variant="outline"
                className="flex-1"
              >
                {isProcessing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <EyeOff className="w-4 h-4 mr-2" />
                )}
                Hide All ({visibleCount})
              </Button>

              <Button
                onClick={handleShowToTenant}
                disabled={isProcessing || hiddenCount === 0}
                className="flex-1 bg-[#00C2C2] hover:bg-[#00A3A3]"
              >
                {isProcessing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Eye className="w-4 h-4 mr-2" />
                )}
                Show All ({hiddenCount})
              </Button>
            </div>
          </div>
        )}

        {results && (
          <div className={`border rounded-lg p-4 ${
            results.errors.length > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-blue-50 border-blue-200'
          }`}>
            <div className="flex items-start gap-2">
              {results.errors.length > 0 ? (
                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              ) : (
                <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1">
                <h4 className={`font-semibold mb-2 ${
                  results.errors.length > 0 ? 'text-yellow-900' : 'text-blue-900'
                }`}>
                  Batch Update Results
                </h4>
                <div className={`text-sm space-y-1 ${
                  results.errors.length > 0 ? 'text-yellow-800' : 'text-blue-800'
                }`}>
                  <p>Total personas: {results.total}</p>
                  <p>Updated: {results.updated}</p>
                  <p>Skipped (already correct state): {results.skipped}</p>
                  {results.errors.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-yellow-200">
                      <p className="font-semibold text-red-600">Errors ({results.errors.length}):</p>
                      <ul className="list-disc pl-5 mt-1">
                        {results.errors.slice(0, 5).map((err, idx) => (
                          <li key={idx}>{err}</li>
                        ))}
                        {results.errors.length > 5 && (
                          <li>...and {results.errors.length - 5} more</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            <strong>Note:</strong> This updates the <code>is_visible</code> field to globally hide/show personas. 
            Updates are processed in batches of 5 with 1-second delays to avoid rate limiting.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}